import CustomTooltip from '../../../shared/CustomTooltip';
import React, { useEffect, useMemo } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    ButtonGroup,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    DeleteOutline,
    NavigateBefore,
    NavigateNext,
    Close,
} from '@mui/icons-material';
import { ModalProps } from '../../../types/modal';
import UserRole from '../../../../shared/AuthRoles';
import {
    useAddModuleAccessMutation,
    useDeleteIndividualModuleAccessMutation,
    useEditModuleAccessMutation,
    useGetAllAssetsQuery,
    useGetIndividualModuleAccessQuery,
    useGetIndividualUserInfoQuery,
    useGetUserListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import {
    camelCaseToTitleCase,
    handleHttpError,
} from '../../../../shared/utils';
import toast from '../../../shared/Toast';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import FormMultiSelectWithALL from '../../../shared/FormMultiSelectWithALL';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import GroupsIcon from '@mui/icons-material/Groups';

export const ROLE_OPTIONS = [
    'company_admin',
    'team_admin',
    'user',
    'view_only_user',
] as const;

type Role = (typeof ROLE_OPTIONS)[number];

const ROLE_LABELS: Record<Role, string> = {
    company_admin: 'Company Admin',
    team_admin: 'Team Admin',
    user: 'User',
    view_only_user: 'view only user',
};

const isRole = (v: unknown): v is Role =>
    (ROLE_OPTIONS as readonly string[]).includes(v as string);

const moduleAccessSchema = z
    .object({
        module: z
            .array(z.object({ id: z.number() }))
            .min(1, 'Experience is required')
            .max(1, 'Select one experience'),
        users: z.array(z.object({ id: z.number() })).default([]),
        roles: z.array(z.enum(ROLE_OPTIONS)).default([]),
    })
    .superRefine((data, ctx) => {
        const hasUsers = (data.users ?? []).length > 0;
        const hasRoles = (data.roles ?? []).length > 0;
        if (!hasUsers && !hasRoles) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Select at least one: Users or Role groups',
                path: ['roles'],
            });
        }
    });

export type ModuleAccessForm = z.infer<typeof moduleAccessSchema>;

interface ModuleAccessPreviewProps extends ModalProps {
    id?: number | null;
    moduleItems?: any[];
    isEditing?: boolean;
    index?: number | null;
    onNavigate?: (dir: 'prev' | 'next') => void;
}

const TOOLTIP_SIZE_SX = { color: 'text.primary', fontSize: 16 } as const;

const VIPModal: React.FC<ModuleAccessPreviewProps> = ({
    id,
    moduleItems,
    isEditing = false,
    open,
    onClose,
    index,
    onNavigate,
}) => {
    const loggedInUser = UserRole();
    const { data: userInfo } = useGetIndividualUserInfoQuery(
        `guests/${loggedInUser.company}/users/${loggedInUser.sub}`,
        { skip: !loggedInUser },
    );

    const isMobile = useMediaQuery('(max-width:700px)');

    const [createModuleAccess] = useAddModuleAccessMutation();
    const [updateModuleAccess] = useEditModuleAccessMutation();
    const [deleteModuleAccess] = useDeleteIndividualModuleAccessMutation();
    const { data: getIndividualModule, isFetching } =
        useGetIndividualModuleAccessQuery(
            `/guests/${loggedInUser?.company}/module-access/${id}`,
        );

    const currentModuleAccess =
        index != null ? moduleItems?.[index ?? 0] : null;

    const total = moduleItems?.length ?? 0;
    const i = index ?? 0;
    const canPrev = total > 0 && i > 0;
    const canNext = total > 0 && i < total - 1;

    const { data: users, isLoading: loadingUsers } = useGetUserListQuery(
        `/guests/${loggedInUser?.company}/users`,
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' && canPrev) onNavigate?.('prev');
            if (e.key === 'ArrowRight' && canNext) onNavigate?.('next');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, canPrev, canNext, onNavigate]);

    const { data: modules } = useGetAllAssetsQuery({ url: '/modules' });

    const methods = useForm<ModuleAccessForm>({
        resolver: zodResolver(moduleAccessSchema),
        mode: 'onChange',
        criteriaMode: 'all',
        defaultValues: { module: [], users: [], roles: [] },
    });

    const {
        control,
        reset,
        handleSubmit,
        setValue,
        getValues,
        trigger,
        clearErrors,
        formState: { isSubmitting, isValid, errors },
    } = methods;

    const userWatch = useWatch({ name: 'users', control });
    const modulesWatch = useWatch({ name: 'module', control });
    const rolesWatch = useWatch({ name: 'roles', control });
    const crossError = errors.roles?.[0]?.message as string | undefined;

    const hasModule = (modulesWatch ?? []).length > 0;
    const hasUsers = (userWatch ?? []).length > 0;
    const hasRoles = (rolesWatch ?? []).length > 0;
    const canSave = hasModule && (hasUsers || hasRoles);

    useEffect(() => {
        if (!open) return;

        if (isEditing && currentModuleAccess) {
            reset({
                module: currentModuleAccess.module?.id
                    ? [{ id: currentModuleAccess.module.id }]
                    : [],
                users: (currentModuleAccess.users ?? []).map((u: any) => ({
                    id: u.id,
                })),
                roles: (currentModuleAccess.roles ?? []).filter((r: string) =>
                    (ROLE_OPTIONS as readonly string[]).includes(r as any),
                ) as Role[],
            });
        } else {
            reset({ module: [], users: [], roles: [] });
        }
    }, [open, isEditing, currentModuleAccess, reset]);

    const normalizeRoleToKey = (role?: string): Role | undefined => {
        if (!role) return undefined;
        const raw = String(role).trim();

        const exact = (ROLE_OPTIONS as readonly string[]).find(
            (r) => r === raw,
        );
        if (exact) return exact as Role;

        const canon = raw
            .toLowerCase()
            .replace(/[^a-z]/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_|_$/g, '');

        const map: Record<string, Role> = {
            company_admin: 'company_admin',
            companyadmin: 'company_admin',
            company_administrator: 'company_admin',
            admin_company: 'company_admin',

            team_admin: 'team_admin',
            teamadmin: 'team_admin',
            admin_team: 'team_admin',

            view_only_user: 'view_only_user',
            view_only: 'view_only_user',
            read_only_user: 'view_only_user',
            viewer: 'view_only_user',

            user: 'user',
            member: 'user',
            basic_user: 'user',
        };

        return map[canon];
    };

    const roleKeyToLabel = (key?: Role) => (key ? ROLE_LABELS[key] : 'Unknown');

    const userList = useMemo(() => {
        const base =
            users?.map((u: any) => ({
                value: u.id,
                label:
                    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
                    u.email,
                ...u,
            })) ?? [];
        return [{ value: 'ALL', label: 'All users' }, ...base];
    }, [users]);

    const closeAndReset = () => {
        onClose?.();
        reset({ module: [], users: [], roles: [] }, { keepDirty: false });
    };

    const toPayload = (form: ModuleAccessForm) => {
        const users = Array.isArray(form.users) ? form.users : [];
        const roles = Array.isArray(form.roles) ? form.roles : [];
        return {
            module: form.module?.[0]?.id
                ? { id: form.module[0].id }
                : undefined,
            users: users
                .filter(
                    (u) =>
                        u && typeof u.id === 'number' && Number.isFinite(u.id),
                )
                .map((u) => ({ id: u.id })),
            roles: roles.filter((r): r is (typeof ROLE_OPTIONS)[number] =>
                (ROLE_OPTIONS as readonly string[]).includes(r as any),
            ),
        };
    };

    const handleCreate = async (form: ModuleAccessForm) => {
        try {
            const payload = toPayload(form);
            await createModuleAccess([
                Number(loggedInUser.company),
                payload,
            ]).unwrap();
            toast.success('VIP Access created');
            reset();
            closeAndReset();
        } catch (error: any) {
            const message =
                error?.data?.message ||
                error?.error ||
                'Error creating VIP Access.';
            toast.error(message);
        }
    };

    const handleUpdate = async (form: ModuleAccessForm) => {
        try {
            if (!currentModuleAccess?.id) {
                toast.error('Missing VIP Access ID');
                return;
            }
            const payload = toPayload(form);
            const res: any = await updateModuleAccess({
                guestId: Number(loggedInUser.company),
                id: currentModuleAccess.id,
                data: payload,
            });
            if (res?.error) {
                toast.error(handleHttpError(res.error));
            } else {
                toast.success('VIP Access updated');
                reset();
                closeAndReset();
            }
        } catch {
            toast.error('Failed to update VIP Access');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            if (!loggedInUser?.company || !id) {
                toast.error('Missing guest or VIP Access ID');
                return;
            }
            const res: any = await deleteModuleAccess({
                guestId: Number(loggedInUser.company),
                id,
            });
            if (res?.error) {
                toast.error(handleHttpError(res.error));
            } else {
                toast.success('VIP Access deleted');
                reset();
                closeAndReset();
            }
        } catch {
            toast.error('Failed to delete VIP Access');
        }
    };

    const onSubmit = (form: ModuleAccessForm) =>
        isEditing ? handleUpdate(form) : handleCreate(form);

    const selectedUsers = useMemo(() => {
        const ids = (Array.isArray(userWatch) ? userWatch : []).map(
            (u: any) => u?.id,
        );
        const set = new Set(ids);
        return userList.filter(
            (opt: any) => typeof opt.value === 'number' && set.has(opt.value),
        );
    }, [userList, userWatch]);

    const roleOptions = useMemo(
        () => [
            { value: 'ALL', label: 'All roles' },
            ...ROLE_OPTIONS.map((r) => ({ value: r, label: ROLE_LABELS[r] })),
        ],
        [],
    );

    const selectedRoleOptions = useMemo(() => {
        const set = new Set(rolesWatch ?? []);
        return roleOptions.filter(
            (opt) => opt.value !== 'ALL' && set.has(opt.value as Role),
        );
    }, [roleOptions, rolesWatch]);

    const selectedModuleValue: string = useMemo(() => {
        const ids = Array.isArray(modulesWatch)
            ? modulesWatch.map((v) => v?.id)
            : [];
        return ids[0] != null ? String(ids[0]) : '';
    }, [modulesWatch]);

    const moduleOptions = useMemo(
        () =>
            (modules ?? [])
                .filter((m: any) => m.isAvailable)
                .map((m: any) => ({
                    value: String(m.id),
                    label: camelCaseToTitleCase(m.name),
                    photoSrc: m.photos?.[0]?.src || '',
                })),
        [modules],
    );

    const loggedInExcluded =
        (rolesWatch?.length > 0 || userWatch?.length > 0) &&
        !rolesWatch?.includes(userInfo?.employeeRole) &&
        !userWatch.map((u) => u.id).includes(userInfo?.id);

    return (
        <Dialog
            open={!!open}
            onClose={closeAndReset}
            maxWidth='md'
            fullWidth
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'auto',
                },
            }}
        >
            <FormProvider
                {...methods}
                key={`${isEditing ? (currentModuleAccess?.id ?? 'new') : 'new'}`}
            >
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    <DialogTitle
                        sx={{
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <Stack
                            spacing={0.5}
                            sx={{
                                minWidth: 0,
                                flex: 1,
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                justifyContent: isMobile
                                    ? 'flex-start'
                                    : 'space-between',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                }}
                            >
                                {isEditing ? (
                                    <Box
                                        sx={{
                                            marginLeft: '5px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        VIP Access Control #
                                        {currentModuleAccess?.id}
                                    </Box>
                                ) : (
                                    <>New VIP Access Control</>
                                )}
                            </Box>

                            {isEditing && !isMobile && (
                                <ButtonGroup
                                    size='small'
                                    variant='outlined'
                                    sx={{ height: 30 }}
                                >
                                    <Button
                                        startIcon={<NavigateBefore />}
                                        onClick={() => onNavigate?.('prev')}
                                        disabled={!canPrev}
                                    >
                                        {!isMobile && 'Prev'}
                                    </Button>
                                    <Button
                                        endIcon={<NavigateNext />}
                                        onClick={() => onNavigate?.('next')}
                                        disabled={!canNext}
                                    >
                                        {!isMobile && 'Next'}
                                    </Button>
                                </ButtonGroup>
                            )}
                        </Stack>

                        <IconButton
                            onClick={closeAndReset}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            px: { xs: 1, sm: 3 },
                        }}
                    >
                        <Stack spacing={1}>
                            <div>
                                <InputLabel
                                    id='module-select-label'
                                    htmlFor='module-select'
                                    required
                                >
                                    Experience{' '}
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                Select the experiences in which
                                                you want to create a VIP Access
                                                list for.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon sx={TOOLTIP_SIZE_SX} />
                                    </CustomTooltip>
                                </InputLabel>
                                <FormControl fullWidth size='small'>
                                    <Select
                                        labelId='module-select-label'
                                        id='module-select'
                                        value={selectedModuleValue}
                                        onChange={(
                                            e: SelectChangeEvent<string>,
                                        ) => {
                                            const v = e.target.value;
                                            setValue(
                                                'module',
                                                v ? [{ id: Number(v) }] : [],
                                                {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                },
                                            );
                                        }}
                                        renderValue={(val) => {
                                            if (val === '')
                                                return (
                                                    <em>Select experience</em>
                                                );
                                            const opt = moduleOptions.find(
                                                (o) => o.value === val,
                                            );
                                            return (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {opt?.photoSrc && (
                                                        <Avatar
                                                            variant='rounded'
                                                            src={opt?.photoSrc}
                                                            sx={{
                                                                width: 24,
                                                                height: 24,
                                                            }}
                                                        />
                                                    )}

                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                '0.875rem',
                                                        }}
                                                    >
                                                        {opt?.label
                                                            ? opt?.label
                                                            : getIndividualModule
                                                                  ?.module.name}
                                                    </Typography>
                                                </Box>
                                            );
                                        }}
                                    >
                                        {moduleOptions.map((opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        src={opt.photoSrc}
                                                        alt={opt.label}
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                '0.875rem',
                                                        }}
                                                    >
                                                        {opt.label}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            <div>
                                <InputLabel htmlFor='users-input'>
                                    Users{' '}
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                Select the users you would like
                                                to exclude. Otherwise select a
                                                role that applies to a group.
                                                You may select both.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon sx={TOOLTIP_SIZE_SX} />
                                    </CustomTooltip>
                                </InputLabel>

                                <FormMultiSelectWithALL
                                    id='users-input'
                                    name='users'
                                    control={control}
                                    options={userList}
                                    value={selectedUsers}
                                    placeholder={
                                        loadingUsers
                                            ? 'Loading…'
                                            : userList.length
                                              ? ''
                                              : 'Select users'
                                    }
                                    getOptionLabel={(o: any) =>
                                        o?.label ??
                                        `${o?.firstName ?? ''} ${
                                            o?.lastName ?? ''
                                        }`.trim()
                                    }
                                    renderOption={(props: any, option: any) => (
                                        <li {...props}>
                                            {option.value === 'ALL' ? (
                                                <ListItemAvatar
                                                    sx={{ minWidth: 32 }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            bgcolor: 'grey.200',
                                                        }}
                                                    >
                                                        <GroupsIcon fontSize='small' />
                                                    </Avatar>
                                                </ListItemAvatar>
                                            ) : (
                                                <ListItemAvatar
                                                    sx={{ minWidth: 32 }}
                                                >
                                                    <Avatar
                                                        variant='rounded'
                                                        src={
                                                            option
                                                                .profilePicture
                                                                ?.src || ''
                                                        }
                                                        alt={option.label}
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                        }}
                                                    />
                                                </ListItemAvatar>
                                            )}
                                            <ListItemText
                                                primary={option.label}
                                                secondary={option.email}
                                            />
                                        </li>
                                    )}
                                    renderTags={(
                                        selected: any[],
                                        getTagProps: any,
                                    ) => {
                                        const MAX = 5;
                                        const visible = (selected ?? []).slice(
                                            0,
                                            MAX,
                                        );
                                        const remainder = Math.max(
                                            0,
                                            (selected ?? []).length -
                                                visible.length,
                                        );

                                        return [
                                            ...visible.map((opt, i) =>
                                                opt.value === 'ALL' ? (
                                                    <Chip
                                                        {...getTagProps({
                                                            index: i,
                                                        })}
                                                        key={`all-${i}`}
                                                        size='small'
                                                        avatar={
                                                            <Avatar
                                                                sx={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    bgcolor:
                                                                        'grey.200',
                                                                }}
                                                            >
                                                                <GroupsIcon fontSize='small' />
                                                            </Avatar>
                                                        }
                                                        label={opt.label}
                                                    />
                                                ) : (
                                                    <Chip
                                                        {...getTagProps({
                                                            index: i,
                                                        })}
                                                        key={opt.value ?? i}
                                                        size='small'
                                                        avatar={
                                                            <Avatar
                                                                src={
                                                                    opt
                                                                        .profilePicture
                                                                        ?.src ||
                                                                    ''
                                                                }
                                                                alt={opt.label}
                                                                sx={{
                                                                    width: 20,
                                                                    height: 20,
                                                                }}
                                                            />
                                                        }
                                                        label={opt.label}
                                                    />
                                                ),
                                            ),
                                            ...(remainder > 0
                                                ? [
                                                      <Chip
                                                          key='users-more'
                                                          size='small'
                                                          label={`+ ${remainder}`}
                                                          sx={{
                                                              fontSize:
                                                                  '0.75rem',
                                                          }}
                                                      />,
                                                  ]
                                                : []),
                                        ];
                                    }}
                                    onChange={(_, selected: any[]) => {
                                        const expandAll = (arr: any[]) =>
                                            arr.some((o) => o?.value === 'ALL')
                                                ? userList
                                                      .filter(
                                                          (u: any) =>
                                                              typeof u.value ===
                                                              'number',
                                                      )
                                                      .map((u: any) => ({
                                                          id: Number(u.value),
                                                      }))
                                                : arr
                                                      .filter(
                                                          (o) =>
                                                              typeof o?.value ===
                                                              'number',
                                                      )
                                                      .map((o) => ({
                                                          id: Number(o.value),
                                                      }));

                                        const cleaned = expandAll(
                                            selected ?? [],
                                        );
                                        const dedup = Array.from(
                                            new Map(
                                                cleaned.map((u: any) => [
                                                    u.id,
                                                    u,
                                                ]),
                                            ).values(),
                                        );

                                        setValue('users', dedup, {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        });

                                        const usersNotEmpty = dedup.length > 0;
                                        const rolesNotEmpty =
                                            (getValues('roles') ?? []).length >
                                            0;

                                        if (usersNotEmpty || rolesNotEmpty) {
                                            clearErrors('roles');
                                        } else {
                                            trigger('roles');
                                        }
                                    }}
                                />
                            </div>

                            <div>
                                <InputLabel htmlFor='role-groups-input'>
                                    Role groups{' '}
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                Select the roles you would like
                                                to exclude. Otherwise select
                                                users. You may select both.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon sx={TOOLTIP_SIZE_SX} />
                                    </CustomTooltip>
                                </InputLabel>

                                <FormMultiAutocomplete
                                    id='role-groups-input'
                                    name='roles'
                                    control={control}
                                    options={roleOptions}
                                    value={selectedRoleOptions}
                                    mapFormValues={(selected) => {
                                        if (
                                            (selected ?? []).some(
                                                (o) => o?.value === 'ALL',
                                            )
                                        ) {
                                            return [...ROLE_OPTIONS];
                                        }

                                        return (selected ?? [])
                                            .map((o) =>
                                                typeof o === 'string'
                                                    ? o
                                                    : o?.value,
                                            )
                                            .filter(isRole);
                                    }}
                                    getOptionLabel={(o: any) => o?.label ?? ''}
                                    isOptionEqualToValue={(
                                        opt: any,
                                        val: any,
                                    ) =>
                                        (opt?.value ?? opt) ===
                                        (val?.value ?? val)
                                    }
                                    renderOption={(props: any, option: any) => (
                                        <li {...props}>
                                            <ListItemText
                                                primary={option.label}
                                            />
                                        </li>
                                    )}
                                    renderTags={(
                                        selected: any[],
                                        getTagProps: any,
                                    ) =>
                                        selected.map((opt, i) => (
                                            <Chip
                                                {...getTagProps({ index: i })}
                                                key={opt.value ?? i}
                                                size='small'
                                                label={opt.label}
                                            />
                                        ))
                                    }
                                    onChange={() => {
                                        const usersNotEmpty =
                                            (getValues('users') ?? []).length >
                                            0;
                                        const rolesNotEmpty =
                                            (getValues('roles') ?? []).length >
                                            0;

                                        if (usersNotEmpty || rolesNotEmpty) {
                                            clearErrors('roles');
                                        } else {
                                            trigger('roles');
                                        }
                                    }}
                                    placeholder={
                                        roleOptions.length
                                            ? ''
                                            : 'No roles available'
                                    }
                                />

                                {crossError && (
                                    <Typography
                                        variant='caption'
                                        color='error'
                                        sx={{ mt: 0.5 }}
                                    >
                                        {crossError}
                                    </Typography>
                                )}
                                {loggedInExcluded && (
                                    <Alert
                                        severity='info'
                                        variant='outlined'
                                        sx={{
                                            mt: 0.75,
                                            py: 0.5,
                                            width: '100%',
                                            overflowWrap: 'break-word',
                                            '& .MuiAlert-message': {
                                                fontSize: '0.8rem',
                                                lineHeight: 1.2,
                                                minWidth: 0,
                                            },
                                        }}
                                    >
                                        Heads up: your role (
                                        <strong>
                                            {roleKeyToLabel(
                                                userInfo?.employeeRole,
                                            )}
                                        </strong>
                                        ) isn’t included in the selected role
                                        group(s), and you’re not listed under
                                        Users, so you won’t see this VIP
                                        listing.
                                    </Alert>
                                )}
                            </div>
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 1,
                            justifyContent: 'space-between',
                        }}
                    >
                        <Stack
                            direction={isMobile ? 'column' : 'row'}
                            spacing={1}
                        >
                            {isEditing && isMobile && (
                                <ButtonGroup
                                    size='small'
                                    variant='outlined'
                                    sx={{ height: 30 }}
                                >
                                    <Button
                                        startIcon={<NavigateBefore />}
                                        onClick={() => onNavigate?.('prev')}
                                        disabled={!canPrev}
                                    >
                                        {!isMobile && 'Prev'}
                                    </Button>
                                    <Button
                                        endIcon={<NavigateNext />}
                                        onClick={() => onNavigate?.('next')}
                                        disabled={!canNext}
                                    >
                                        {!isMobile && 'Next'}
                                    </Button>
                                </ButtonGroup>
                            )}
                            {!isMobile &&
                                typeof index === 'number' &&
                                (moduleItems?.length ?? 0) > 0 && (
                                    <Typography
                                        component='span'
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: isMobile ? 9 : 14,
                                        }}
                                    >
                                        ({index! + 1} of {moduleItems!.length}{' '}
                                        items)
                                    </Typography>
                                )}
                        </Stack>

                        <Stack
                            direction='row'
                            spacing={0.5}
                            sx={{ '& > *': { marginLeft: 0.5 } }}
                        >
                            {isEditing && (
                                <CustomIconButton
                                    variant='contained'
                                    onClick={() =>
                                        handleDelete(currentModuleAccess?.id)
                                    }
                                    sx={{
                                        '&:hover': {
                                            cursor: 'pointer',
                                            color: 'red',
                                            backgroundColor: 'transparent',
                                            background: 'none',
                                        },
                                    }}
                                >
                                    <DeleteOutline sx={{ fontSize: '16px' }} />
                                </CustomIconButton>
                            )}

                            <Button
                                type='button'
                                variant='outlined'
                                color='primary'
                                onClick={closeAndReset}
                            >
                                {isEditing ? 'Close' : 'Cancel'}
                            </Button>

                            <CustomButton
                                variant='contained'
                                color='primary'
                                type='submit'
                                disabled={isSubmitting || !canSave}
                            >
                                {isEditing ? 'Update' : 'Save'}
                            </CustomButton>
                        </Stack>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default VIPModal;
